/** @jsxImportSource @emotion/react */
import { Add, Check, Close, Error, ReceiptLong, Remove, SearchOff, Settings } from '@mui/icons-material';
import { Box, Chip, Container, Grid, IconButton, Stack, Typography, useTheme } from '@mui/material';
import { DataGrid, GridCellParams, GridColDef, GridSortItem, GridSortModel, GridValueFormatterParams } from '@mui/x-data-grid';
import { useEffect, useState } from 'react';

import { stripedDateGrid } from '../../../common/CustumEmotionStyles';
import { numberRoundTwoDecimals, numberWithSpaces, remToPixels } from '../../../common/CustomFunctions';
import { allowedSort, IFileSortItem, ISourceDocumentModel } from '../../../service/model/ISourceDocumentModel';
import { useTranslation } from 'react-i18next';
import SourceDocumentService from '../../../service/SourceDocumentService';

interface IProps {
    //rows: ISourceDocumentModel[] | null,
    //errors: string[],
    //limitPerPage: number,
    //totalItems: number,
    //loader: boolean,
    //changePage: (page: number) => void
    //sortItem: (sortItem: IFileSortItem | null) => void,
}

interface IRows {

    id: number,
    indent: boolean,
    name: string,
    awaitingDataEntry: string,
    inProgress: string,
    awaitingTransfer: string,
}

function onlyUnique(value: any, index: number, self: any) {
    return self.indexOf(value) === index;
}
function sortDate(a: string, b: string) {
    return new Date(b).getTime() - new Date(a).getTime()
}

const DatagridPendingWork = (props: IProps): JSX.Element => {

    const theme = useTheme();
    const [gridRows, setGridRows] = useState<IRows[]>([]);
    const [gridError, setGridError] = useState(false);
    const [loader, setLoader] = useState(true);

    useEffect(() => {

        let isMounted = true; //Avoid memory leaks. Async function running after dismount.

        setLoader(true);

        const service = new SourceDocumentService();

        const getSourceDocumentStatistics = async () => {

            const result = await service.getSourceDocumentStatistics();
            if (result.hasErrors) {
                setGridError(true)
            } else {
                setGridError(false);
                let tempList: IRows[] = [];

                let q = 0;

                for (let i = 0; i < result.value.length; i++) {

                    const awaitingDataEntryDate = result.value[i].awaitingForDataEntryLast ? new Date(result.value[i].awaitingForDataEntryLast) : null;
                    const awaitingDataEntryText = awaitingDataEntryDate ? " (" + awaitingDataEntryDate?.toLocaleDateString() + ")" : "";

                    const inProgressDate = result.value[i].inProgressLast ? new Date(result.value[i].inProgressLast) : null;
                    const inProgressText = inProgressDate ? " (" + inProgressDate?.toLocaleDateString() + ")" : "";

                    const awaitingTransferDate = result.value[i].awaitingForTransferLast ? new Date(result.value[i].awaitingForTransferLast) : null;
                    const awaitingTransferText = awaitingTransferDate ? " (" + awaitingTransferDate?.toLocaleDateString() + ")" : "";

                    tempList.push({
                        id: q++,
                        indent: false,
                        name: result.value[i].name,
                        awaitingDataEntry: result.value[i].awaitingForDataEntryCount + awaitingDataEntryText,
                        inProgress: result.value[i].inProgressCount + inProgressText,
                        awaitingTransfer: result.value[i].awaitingForTransferCount + awaitingTransferText, 
                        })

                    const subRows = result.value[i].typeStatistics;

                    for (let j = 0; j < subRows.length; j++) {

                        const awaitingDataEntryDate = subRows[j].awaitingForDataEntryLast ? new Date(subRows[j].awaitingForDataEntryLast) : null;
                        const awaitingDataEntryText = awaitingDataEntryDate ? " (" + awaitingDataEntryDate?.toLocaleDateString() + ")" : "";

                        const inProgressDate = subRows[j].inProgressLast ? new Date(subRows[j].inProgressLast) : null;
                        const inProgressText = inProgressDate ? " (" + inProgressDate?.toLocaleDateString() + ")" : "";

                        const awaitingTransferDate = subRows[j].awaitingForTransferLast ? new Date(subRows[j].awaitingForTransferLast) : null;
                        const awaitingTransferText = awaitingTransferDate ? " (" + awaitingTransferDate?.toLocaleDateString() + ")" : "";

                        tempList.push({
                            id: q++,
                            indent: true,
                            name: subRows[j].sourceDocumentType,
                            awaitingDataEntry: subRows[j].awaitingForDataEntryCount + awaitingDataEntryText,
                            inProgress: subRows[j].inProgressCount + inProgressText,
                            awaitingTransfer: subRows[j].awaitingForTransferCount + awaitingTransferText,
                        })

                    }
                }
                setGridRows(tempList);
            }
            setLoader(false);
        }

        getSourceDocumentStatistics();

        return () => { isMounted = false };

    }, [])

    const { t, i18n } = useTranslation();

    function indent(params: GridCellParams) {
        if (params.row.indent === false) {
            return <Typography variant="body1">{params.value}</Typography>;
        } else {
            return <Typography variant="body2">&nbsp;&nbsp;&nbsp;{params.value}</Typography>;
        }
    }

    const columns: GridColDef[] = [
        {
            field: 'id', headerName: 'id', width: 60, hide: true,
            //renderCell: () => (<Check fontSize="large" color="success" />)
        },
        {
            field: 'name', headerName: "", minWidth: 160, flex: 1, renderCell: (params: GridCellParams) => indent(params), sortable:false,
        },
        {
            field: 'awaitingDataEntry', headerName: t('dashboard.awaitingDataEntry'), width: 250, sortable: false
        },
        {
            field: 'inProgress', headerName: t('dashboard.inProgress'), width: 250, sortable: false
        },
        {
            field: 'awaitingTransfer', headerName: t('dashboard.awaitingTransfer'), width: 250, sortable: false
        },
    ];


    function CustomErrorOverlay() {
        return <Grid container direction="column" justifyContent="center" alignItems="center" height="100%">
            <Grid item><Error fontSize="large" /></Grid>
            <Grid item width="15rem" display="flex" justifyContent="center">
                <Typography variant="body2">
                    {t("basic.unexpectedError")}
                </Typography>
            </Grid>
        </Grid>
    }
    function CustomNoRowsOverlay() {
        return <Container sx={{ display: "flex", height: "100%", justifyContent: "center", alignItems: "center" }}>
            <Stack direction="column">
                <SearchOff fontSize="large" />
                <Typography>No documents!</Typography>
            </Stack>
        </Container>
    }

    //const [sortModel, setSortModel] = useState<GridSortModel | undefined>()
    //const handleSortModelChange = (newModel: GridSortModel) => {
    //    setSortModel(newModel);
    //    //This datagrid only handles single column filter.
    //    if (newModel.length === 1 && (newModel[0].sort === "desc" || newModel[0].sort === "asc")) {
    //        let sItem: IFileSortItem = {field: newModel[0].field, sort: newModel[0].sort}
    //        props.sortItem(sItem); //Add filter
    //    } else{
    //        props.sortItem(null); //Remove filter. //Does not call API if empty string from before.
    //    }
    //};

    return <DataGrid css={stripedDateGrid(theme, theme.palette.grey[200])}
        rows={gridRows}
        columns={columns}
        hideFooter
        disableColumnFilter
        disableColumnMenu
        rowHeight={remToPixels(2)}
        loading={loader}
        headerHeight={remToPixels(4)}
        getRowClassName={(params) =>
            params.row.indent === false ? "even" : "odd"
        }
        error={gridError ? true : undefined}
        components={{
            ErrorOverlay: CustomErrorOverlay,
            NoRowsOverlay: CustomNoRowsOverlay,
        }} />
}

export default DatagridPendingWork;